import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

import AdmHero from "@/components/admin/AdmHero";
import AdmAbout from "@/components/admin/AdmAbout";
import AdmProjects from "@/components/admin/AdmProjects";
import AdmSkills from "@/components/admin/AdmSkills";
import AdmExperience from "@/components/admin/AdmExperience";
import AdmProblemsSolved from "@/components/admin/AdmProblemsSolved";
import AdmDevOpsShowcase from "@/components/admin/AdmDevOpsShowcase";
import AdmContact from "@/components/admin/AdmContact";
import AdmChatbot from "@/components/admin/AdmChatbot";
import AdmNotifications from "@/components/admin/AdmNotifications";

import { User, Briefcase, Factory, Home, Contact, Cpu, Sparkles, Wrench, MessageCircle, LogOut, Loader2, Bell } from "lucide-react";
import { toast } from "sonner";

const Admin = () => {
    const { logout } = useAuth();
    const navigate = useNavigate();
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    
    const adminSections = [
        { id: 'admin-hero', Icon: <Home />, label: 'Hero', component: <AdmHero /> },
        { id: 'admin-about', Icon: <User />, label: 'About', component: <AdmAbout /> },
        { id: 'admin-projects', Icon: <Briefcase />, label: 'Projects', component: <AdmProjects /> },
        { id: 'admin-skills', Icon: <Wrench />, label: 'Skills', component: <AdmSkills /> },
        { id: 'admin-experience', Icon: <Factory />, label: 'Experience', component: <AdmExperience /> },
        { id: 'admin-problems-solved', Icon: <Sparkles />, label: 'Problems Solved', component: <AdmProblemsSolved /> },
        { id: 'admin-devops-showcase', Icon: <Cpu />, label: 'DevOps Showcase', component: <AdmDevOpsShowcase /> },
        { id: 'admin-contact', Icon: <Contact />, label: 'Contact', component: <AdmContact /> },
        { id: 'admin-chatbot', Icon: <MessageCircle />, label: 'Chatbot', component: <AdmChatbot /> },
        { id: 'admin-notifications', Icon: <Bell />, label: 'Notifications', component: <AdmNotifications /> }
    ];

    const handleLogout = async () => {
        setIsLoggingOut(true);
        try {
            await logout();
            toast.success('Déconnexion réussie');
            navigate('/auth');
        } catch (error) {
            toast.error('Erreur lors de la déconnexion');
            // console.error('Logout error:', error);
        } finally {
            setIsLoggingOut(false);
        }
    };

    const [activeTab, setActiveTab] = useState('admin-hero');

    return (
        <>
            <Header />
            <div className="min-h-screen max-w-7xl mx-auto transition-colors duration-300 flex">
                <aside className="w-64 bg-white dark:bg-[#0a0e1a] border-r border-gray-200 dark:border-gray-700 h-screen sticky top-16">
                    <div className="p-3">
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Admin Panel</h2>
                        <nav className="space-y-1">
                            {adminSections.map((section) => (
                                <button
                                    key={section.id}
                                    onClick={() => setActiveTab(section.id)}
                                    className={`w-full text-left text-md px-3 py-2 rounded-sm transition-all duration-200 flex items-center space-x-3 ${activeTab === section.id
                                        ? 'bg-emerald-500 text-white shadow-lg'
                                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                                        }`}
                                >
                                    <span className="font-bold text-sm">{section.Icon}</span>
                                    <span className="font-medium">{section.label}</span>
                                </button>
                            ))}
                        </nav>
                        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                            <button
                                onClick={handleLogout}
                                disabled={isLoggingOut}
                                className="w-full text-left text-md px-3 py-2 rounded-sm transition-all duration-200 flex items-center space-x-3 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <span className="font-bold text-sm">
                                    {isLoggingOut ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogOut className="w-4 h-4" />}
                                </span>
                                <span className="font-medium">{isLoggingOut ? 'Déconnexion...' : 'Déconnexion'}</span>
                            </button>
                        </div>
                    </div>
                </aside>

                <main className="flex-1">
                    <section className="py-24 bg-white dark:bg-[#0a0e1a] min-h-screen">
                        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                            <div className="max-w-6xl mx-auto">
                                {adminSections.find(section => section.id === activeTab)?.component}
                            </div>
                        </div>
                    </section>
                </main>
            </div>
            <Footer />
        </>
    );
};

export default Admin;